<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="Immmigration Benefit"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col>
          <v-autocomplete
            v-model="section.benefit_type"
            :items="optionsBenefitTypes"
            label="Benefit Type"
            outlined
            :error-messages="getErrorMessages(v$.section.benefit_type)"
            @input="v$.section.benefit_type.$touch()"
            @blur="v$.section.benefit_type.$touch()"
          />
        </v-col>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Initiated Date"
            :formatable-date="section.initiated_date"
            :format="section.initiated_date_format"
            :uncertain="section.initiated_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.initiated_date)"
            @update-box="onInitiatedDateUncertainUpdate"
            @update-date="onInitiatedDateUpdate"
            @tab-out="v$.section.initiated_date.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Outcome Date"
            :formatable-date="section.outcome_date"
            :format="section.outcome_date_format"
            :uncertain="section.outcome_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages(v$.section.outcome_date)"
            @update-box="onOutcomeDateUncertainUpdate"
            @update-date="onOutcomeDateUpdate"
            @tab-out="v$.section.outcome_date.$touch()"
          />
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="section.benefit_outcome"
            :items="optionsBenefitOutcomes"
            label="Benefit Outcome"
            outlined
            :error-messages="getErrorMessages(v$.section.benefit_outcome)"
            @input="v$.section.benefit_outcome.$touch()"
            @blur="v$.section.benefit_outcome.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <v-autocomplete
            v-model="section.a_number"
            :items="optionsAvailableANumbers"
            label="A#"
            outlined
            :error-messages="getErrorMessages(v$.section.a_number)"
            @input="v$.section.a_number.$touch()"
            @blur="v$.section.a_number.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="section.nvc_number"
            autocomplete="off"
            label="NVC Number"
            outlined
            :error-messages="getErrorMessages(v$.section.nvc_number)"
            @input="v$.section.nvc_number.$touch()"
            @blur="v$.section.nvc_number.$touch()"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="section.other_identifier"
            autocomplete="off"
            label="Other Identifier"
            outlined
            :error-messages="getErrorMessages(v$.section.other_identifier)"
            @input="v$.section.other_identifier.$touch()"
            @blur="v$.section.other_identifier.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <!-- text-area notes -->
        <v-col>
          <v-textarea
            v-model="section.notes"
            label="Notes"
            rows="4"
            outlined
            :error-messages="getErrorMessages(v$.section.notes)"
            @input="v$.section.notes.$touch()"
            @blur="v$.section.notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'ImmigrationBenefitsCard',
  components: { FormatSelectableComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {
        a_number: {},
        benefit_outcome: {},
        benefit_type: {},
        initiated_date: {},
        initiated_date_uncertain: {},
        notes: {},
        nvc_number: {},
        other_identifier: {},
        outcome_date: {},
        outcome_date_uncertain: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return `immigration-benefits-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peImmigrationBenefitsCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeImmigrationBenefitsCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    optionsAvailableANumbers () {
      return this.getCachedDropdown(`parties/${this.peParty?.id}/available_a_numbers`)
    },

    optionsBenefitOutcomes () {
      return this.getCachedDropdown('immigration_benefits/benefit_outcome')
    },

    optionsBenefitTypes () {
      return this.getCachedDropdown('immigration_benefits/benefit_type')
    },

    sectionIdentifier () { return this.section.id || 'new' },

    ...mapGetters(PARTY_NAMESPACE, [
      'peImmigrationBenefitsCardLock',
      'peParty',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeImmigrationBenefitsCard(this.sectionIdentifier)

    this.getDropdownOptions({ path: 'immigration_benefits/benefit_type' })
    this.getDropdownOptions({ path: 'immigration_benefits/benefit_outcome' })
    this.getDropdownOptions({ force: true, options: false, path: `parties/${this.peParty.id}/available_a_numbers` })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peImmigrationBenefitsCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peImmigrationBenefitsCardCreateUndo()
      else {
        return this.peImmigrationBenefitsCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peImmigrationBenefitsCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peImmigrationBenefitsCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    onInitiatedDateUncertainUpdate (value) {
      this.v$.section.initiated_date.$touch()
      this.v$.section.initiated_date_uncertain.$touch()
      this.section.initiated_date_uncertain = value
    },

    onInitiatedDateUpdate (value) {
      this.v$.section.initiated_date.$touch()
      this.v$.section.initiated_date_uncertain.$touch()
      this.section.initiated_date = value.date
      this.section.initiated_date_format = value.format
    },

    onOutcomeDateUncertainUpdate (value) {
      this.v$.section.outcome_date.$touch()
      this.v$.section.outcome_date_uncertain.$touch()
      this.section.outcome_date_uncertain = value
    },

    onOutcomeDateUpdate (value) {
      this.v$.section.outcome_date.$touch()
      this.v$.section.outcome_date_uncertain.$touch()
      this.section.outcome_date = value.date
      this.section.outcome_date_format = value.format
    },

    async onSectionUnlock () {
      return this.setPeImmigrationBenefitsCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peImmigrationBenefitsCardCreateUndo',
      'setPeImmigrationBenefitsCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeImmigrationBenefitsCard',
      'peImmigrationBenefitsCardCreate',
      'peImmigrationBenefitsCardDelete',
      'peImmigrationBenefitsCardRefresh',
      'peImmigrationBenefitsCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
